import { useTheme } from "@/hooks/useTheme";

const bigMetrics = [
  { value: "3×", label: "Faster Lead Response", sub: "compared to industry average" },
  { value: "85%", label: "Reduction in Manual Tasks", sub: "through intelligent automation" },
  { value: "32%", label: "More Booked Jobs", sub: "within the first 90 days" },
  { value: "41%", label: "Higher Application Completions", sub: "driven by instant follow-up" },
];

const results = [
  { icon: "ri-calendar-check-line", headline: "More Booked Appointments", detail: "Automated scheduling fills your calendar without your team lifting a finger." },
  { icon: "ri-line-chart-line", headline: "Higher Conversion Rates", detail: "Instant responses and intelligent follow-ups turn more leads into paying clients." },
  { icon: "ri-flashlight-line", headline: "Faster Response Times", detail: "Go from hours to seconds — never lose a deal to a faster competitor." },
  { icon: "ri-shield-check-line", headline: "Reduced Operational Gaps", detail: "Systematic coverage means no lead is forgotten, no follow-up is missed." },
  { icon: "ri-arrow-up-line", headline: "Predictable, Scalable Growth", detail: "A reliable system means your growth is determined by strategy, not by luck." },
];

const caseStudies = [
  {
    industry: "Construction",
    icon: "ri-building-2-line",
    headline: "Construction Firm Increased Booked Jobs by 32%",
    outcome: "By automating their inbound inquiry process and implementing smart dispatch routing, this regional construction firm converted 32% more inquiries into booked projects within the first 90 days.",
    metrics: [{ label: "Booked Jobs", value: "+32%" }, { label: "Response Time", value: "-78%" }],
    image: "https://public.readdy.ai/ai/img_res/9261df4e5957a2149dabbc404b9df844.jpg",
  },
  {
    industry: "Financial Services",
    icon: "ri-bank-line",
    headline: "Lending Team Reduced Response Time by 85%",
    outcome: "A regional lending team replaced manual lead follow-up with automated response, cutting average response time from 4 hours to under 20 minutes — resulting in significantly higher applicant completion rates.",
    metrics: [{ label: "Response Time", value: "-85%" }, { label: "Application Completions", value: "+41%" }],
    image: "https://readdy.ai/api/search-image?query=professional%20financial%20lending%20team%20celebrating%20successful%20loan%20processing%20transformation%20results%20in%20sleek%20modern%20banking%20office%2C%20high-performing%20team%20reviewing%20impressive%20metrics%20on%20screens%2C%20warm%20confident%20corporate%20atmosphere%20with%20premium%20financial%20services%20aesthetic%2C%20business%20success%20and%20growth%20concept&width=800&height=520&seq=cs_fin_v2&orientation=landscape",
  },
];

export default function ResultsSection() {
  const { isDark } = useTheme();

  const bg = isDark ? "#120720" : "#f0e9de";
  const bgWhite = isDark ? "#0d0818" : "white";
  const cardBg = isDark ? "#1a0d28" : "white";
  const cardBorder = isDark ? "rgba(255,255,255,0.09)" : "#e4d8c8";
  const headingColor = isDark ? "#e4d0f8" : "#4d316c";
  const subheadingColor = isDark ? "#c4aad8" : "#705a89";
  const bodyColor = isDark ? "rgba(255,255,255,0.58)" : "#7a6b80";
  const labelColor = isDark ? "#a9baab" : "#869b8b";
  const iconBg = isDark ? "rgba(169,186,171,0.12)" : "#f0e9de";
  const caseStudyContentBg = isDark ? "#1a0d28" : "#faf8f5";
  const caseStudyBorder = isDark ? "rgba(255,255,255,0.09)" : "#e4d8c8";
  const resultHeadlineColor = isDark ? "#e4d0f8" : "#4d316c";
  const imageBorder = isDark ? "rgba(255,255,255,0.09)" : "#e4d8c8";

  return (
    <>
      {/* Giant Metrics Section */}
      <section id="results" className="py-28 md:py-40 overflow-hidden transition-colors duration-300" style={{ background: bg }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-20">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase mb-5" style={{ color: labelColor }}>
              Business Outcomes
            </span>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight tracking-tight mb-5" style={{ color: headingColor }}>
              What This Means
              <br />
              <span style={{ color: subheadingColor }}>for Your Business.</span>
            </h2>
            <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: bodyColor }}>
              When operations are fully systemized, the results aren&apos;t incremental — they&apos;re transformational.
            </p>
          </div>

          {/* Apple-style giant metrics grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-20">
            {bigMetrics.map((m, i) => (
              <div
                key={m.label}
                className="flex flex-col items-center text-center py-12 px-6 rounded-3xl transition-colors duration-300"
                style={{
                  background: i % 2 === 0 ? "#4d316c" : cardBg,
                  border: `1px solid ${i % 2 === 0 ? "transparent" : cardBorder}`,
                }}
              >
                <span
                  className="text-5xl md:text-6xl font-semibold tracking-tight mb-3"
                  style={{ color: i % 2 === 0 ? "#a9baab" : headingColor }}
                >
                  {m.value}
                </span>
                <span
                  className="text-sm font-semibold mb-1"
                  style={{ color: i % 2 === 0 ? "white" : headingColor }}
                >
                  {m.label}
                </span>
                <span
                  className="text-xs leading-snug"
                  style={{ color: i % 2 === 0 ? "rgba(255,255,255,0.45)" : labelColor }}
                >
                  {m.sub}
                </span>
              </div>
            ))}
          </div>

          {/* Results list + visual */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="flex flex-col gap-4">
              {results.map((r) => (
                <div
                  key={r.headline}
                  className="flex items-start gap-4 p-5 rounded-2xl transition-colors duration-300"
                  style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                >
                  <div
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl"
                    style={{ background: iconBg }}
                  >
                    <i className={`${r.icon} text-base`} style={{ color: isDark ? "#c4aad8" : "#4d316c" }}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1" style={{ color: resultHeadlineColor }}>{r.headline}</p>
                    <p className="text-xs leading-relaxed" style={{ color: bodyColor }}>{r.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative w-full rounded-3xl overflow-hidden" style={{ border: `1px solid ${imageBorder}` }}>
              <img
                src="https://readdy.ai/api/search-image?query=enterprise%20analytics%20dashboard%20showing%20dramatic%20business%20performance%20transformation%20with%20rising%20conversion%20metrics%2C%20booked%20appointments%20calendar%20fully%20filled%2C%20response%20time%20charts%20showing%20massive%20improvement%2C%20clean%20premium%20SaaS%20reporting%20interface%20in%20deep%20violet%20and%20sage%20green%20color%20palette%20on%20clean%20background&width=900&height=720&seq=results_dash_v2&orientation=portrait"
                alt="Business results dashboard"
                className="w-full object-cover"
                style={{ height: "500px" }}
              />
              <div
                className="absolute bottom-0 left-0 right-0 p-6"
                style={{ background: "linear-gradient(to top, rgba(77,49,108,0.85), transparent)" }}
              >
                <p className="text-white font-semibold text-base">Your results, systemized.</p>
                <p className="text-white/55 text-xs mt-1">Real-time visibility into every metric that matters.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-28 md:py-40 transition-colors duration-300" style={{ background: bgWhite }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase mb-5" style={{ color: labelColor }}>
              Case Studies
            </span>
            <h2 className="text-4xl md:text-6xl font-semibold leading-tight tracking-tight" style={{ color: headingColor }}>
              Real Systems. Real Outcomes.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((cs) => (
              <div
                key={cs.headline}
                className="group flex flex-col rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-default"
                style={{ border: `1px solid ${caseStudyBorder}` }}
              >
                <div className="relative w-full overflow-hidden" style={{ height: "300px" }}>
                  <img
                    src={cs.image}
                    alt={cs.headline}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-5 left-5 flex gap-4">
                    {cs.metrics.map((m) => (
                      <div
                        key={m.label}
                        className="px-4 py-2.5 rounded-xl"
                        style={{ background: "rgba(240,233,222,0.92)", backdropFilter: "blur(8px)" }}
                      >
                        <div className="text-lg font-semibold leading-none" style={{ color: "#4d316c" }}>{m.value}</div>
                        <div className="text-xs mt-0.5" style={{ color: "#869b8b" }}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                  <div
                    className="absolute top-5 right-5 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                    style={{ background: "rgba(77,49,108,0.8)", color: "#c4aad8", backdropFilter: "blur(8px)" }}
                  >
                    <i className={`${cs.icon} text-xs`}></i>
                    {cs.industry}
                  </div>
                </div>

                <div className="p-8 flex flex-col transition-colors duration-300" style={{ background: caseStudyContentBg }}>
                  <h3 className="font-semibold text-lg mb-3 leading-snug" style={{ color: headingColor }}>
                    {cs.headline}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: bodyColor }}>
                    {cs.outcome}
                  </p>
                  <div className="mt-6 flex items-center gap-2" style={{ color: labelColor }}>
                    <span className="text-xs font-semibold tracking-wide">Read case study</span>
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
